import { ITEM_PROMO, ORDER_PROMO, PROD_DOMAIN } from './constants';

const formatForThousand = (dollars) => {
  if (dollars.length >= 4) {
    const hundreds = dollars.slice(-3);
    const thousands = dollars.slice(0, -3);
    return `${thousands},${hundreds}`;
  }
  return dollars;
};

const reformatPrice = (price) => {
  if (!price) return '';
  const splitPrice = price.split('.');
  if (splitPrice[0].length >= 4) {
    const dollars = formatForThousand(splitPrice[0]);
    const cents = splitPrice[1] || '00';
    return `${dollars}.${cents}`;
  }
  return `${price}`;
};

const deconstructPrice = (price) => {
  const priceParts = {};
  if (!price) return '';
  const priceSplit = price.toString().split('.');
  priceParts.dollars = formatForThousand(priceSplit[0]);
  priceParts.cents = priceSplit[1] || '00';
  if (priceParts.cents.length === 1) {
    priceParts.cents += '0';
  }
  return priceParts;
};

export const getUniquePromoIds = (promos) => {
  const promoIds = promos?.filter((promo) => !!promo.promoId).map((promo) => promo.promoId) || [];
  return [...new Set(promoIds)];
};

const getSavingLabels = (promos) => {
  const promoIds = getUniquePromoIds(promos);
  return promoIds?.map((id) => promos.find((promo) => promo.promoId === id).description?.shortDesc || ''
  ) || [];
};

const getSavings = (promos) => {
  return promos?.map((promotion) => promotion.dollarOff)
    .reduce((savings, dollarOff) => savings + Number.parseFloat(dollarOff), 0) || 0;
};

const calculatePromotionalAdjustments = (ids, products) => {

  const promotionalAdjustments = Array.from(products.values())
    .filter((product) => ids.includes(product.identifiers?.itemId) && !!product.pricing?.promotionalAdjustments)
    .flatMap((product) => product.pricing.promotionalAdjustments);

  const filteredPromos = promotionalAdjustments?.filter((promo) => {
    return (!!promo?.dollarOff && !Number.isNaN(promo.dollarOff));
  });

  const bundlePromos = filteredPromos?.filter((promotion) => ITEM_PROMO === promotion.type);
  const additionalPromos = filteredPromos?.filter((promotion) => ORDER_PROMO === promotion.type);
  const bundleSavings = getSavings(bundlePromos);
  const additionalSavings = getSavings(additionalPromos);

  return {
    bundleSavings,
    additionalSavings,
    hasBundleSavings: bundleSavings > 0,
    hasAdditionalSavings: additionalSavings > 0,
    bundleSavingsLabels: bundleSavings > 0 ? getSavingLabels(bundlePromos) : [],
    additionalSavingsLabels: additionalSavings > 0 ? getSavingLabels(additionalPromos) : []
  };
};

const calculateTotal = (ids, products) => {
  const template = {
    mapExistsInPackage: false,
    original: 0,
    savings: 0,
    value: 0
  };
  let calculatedTotal = Array.from(products.values()).reduce((total, item) => {
    if (!ids.includes(item.identifiers?.itemId)) return total;

    const pricing = item.pricing || null;
    if (!pricing) return total;

    const {
      mapAboveOriginalPrice, original, value, promotion = {}, mapDetail = {}
    } = pricing || {};
    const mapPolicy = mapDetail?.mapPolicy || null;

    let perItemPrice;

    if (mapPolicy !== null) {
      perItemPrice = value || original;
    } else if (mapAboveOriginalPrice !== null && mapAboveOriginalPrice !== undefined) {
      perItemPrice = original;
    } else {
      perItemPrice = value;
    }

    /* eslint-disable */
    total.original += (original || perItemPrice || 0) * item.selectedQty;
    total.value += (value || perItemPrice || 0) * item.selectedQty;
    total.savings += (promotion?.dollarOff || 0) * item.selectedQty;

    if (mapAboveOriginalPrice !== null || mapPolicy !== null) {
      total.mapExistsInPackage = true;
    }

    return total;
  }, template);

  const adjustments = calculatePromotionalAdjustments(ids, products);
  const totalValue = calculatedTotal.value - adjustments.bundleSavings - adjustments.additionalSavings;
  const totalSavings = calculatedTotal.savings + adjustments.bundleSavings + adjustments.additionalSavings;
  const [bundleSavingLabel = ''] = adjustments.bundleSavingsLabels;
  const [additionalSavingLabel = ''] = adjustments.additionalSavingsLabels;
  return {
    originalValueRaw: calculatedTotal.original,
    original: reformatPrice(calculatedTotal.original.toFixed(2)),
    value: deconstructPrice(calculatedTotal.value.toFixed(2)),
    totalValue: deconstructPrice(totalValue.toFixed(2)),
    totalValueRaw: totalValue,
    bundleSavings: reformatPrice(adjustments.bundleSavings.toFixed(2)),
    additionalSavings: reformatPrice(adjustments.additionalSavings.toFixed(2)),
    totalSavings: reformatPrice(totalSavings.toFixed(2)),
    hasBundleSavings: adjustments.hasBundleSavings,
    hasAdditionalSavings: adjustments.hasAdditionalSavings,
    bundleLabel: bundleSavingLabel,
    additionalLabel: additionalSavingLabel,
    hasTotalSavings: totalSavings > 0,
    totalPercentage: ((totalSavings / calculatedTotal.original) * 100).toFixed(0),
    mapExistsInPackage: calculatedTotal.mapExistsInPackage
  };
};

/*
  Takes any number of functions and passes one value through all of them.
  Except the first function which can accept any number of arguments

  const addOne = (num) => num + 1;

  const result = pipe(addOne)(4) --> 5;
  is equivalent to
  const result = addOne(4); --> 5

  const result = pipe(addOne, addOne)(4) --> 6
  is equivalent to
  const result = addOne(addOne(4)) --> 6
 */
const pipe = (...functions) => {
  return functions?.reduce((currentValue, currentFunction) => {
    return (...params) => currentFunction(currentValue(...params));
  });
};

/*
  Accepts a state argument and any number of additional arguments.
  First argument to each function will be either:
    1. initialState argument passed in if function is the first function
    2. the return from the previous function
  Any additional arguments will be passed in to every function

  const adder = (num1, num2 = 0, num3 = 0) => num1 + num2 + num3;

  const result = reducerPipe(adder)(1, 2, 3) --> 6;
  is equivalent to
  const result = adder(1, 2, 3) --> 6;

  const result = reducerPipe(adder, adder)(1, 2, 3) --> 11;
  is equivalent to
  const result = adder(adder(1, 2, 3), 2, 3) --> 11;
 */
const reducerPipe = (...funcs) => (state, ...args) => funcs?.reduce((newState, func) => func(newState, ...args), state);

const calculateTotalPrice = calculateTotal;

const createDefaultVariables = (storeId, defaultVariables) => ({
  storeId,
  ...(typeof defaultVariables?.current?.isBrandPricingPolicyCompliant === 'boolean' && {
    isBrandPricingPolicyCompliant: defaultVariables?.current?.isBrandPricingPolicyCompliant
  })
});

const getUrl = (path) => {
  let url = '';
  const host = window?.location?.host;
  if (typeof host === 'string' && (
    host.includes('hd-qa74') || host.includes('hd-qa71') || host.includes('storefront')
  )) {
    url = getProtocol() + host + path;
  } else {
    url = PROD_DOMAIN + path;
  }
  return url;
};

const signIn = () => {
  return window?.open(
    getUrl(`/auth/view/signin?redirect=${window?.location?.pathname}`), '_self', 'noopener, noreferrer'
  );
};

export {
  pipe,
  reducerPipe,
  reformatPrice,
  calculateTotalPrice,
  deconstructPrice,
  formatForThousand,
  createDefaultVariables,
  signIn
};
